import { Dialog, useMediaQuery, useTheme } from '@mui/material'
import {
  useListInvoicedocumentsQuery,
  useListPaymentDocumentsQuery,
} from 'store/invoices/invoices.api'
import InvoiceDocumentsTable from './invoice-documents-table/invoice-documents-table.component'
import { skipToken } from '@reduxjs/toolkit/query'
import { InvoiceType, ListDocumentsResponse } from 'store/invoices/invoices.types'
import { useMemo } from 'react'

type InvoiceDocumentsDialogProps = {
  orderId: string
  invoiceId: string
  invoiceType: InvoiceType
  open: boolean
  handleClose: () => void
  /** Carrier payables: always show payment/other docs; hide waybill. */
  variant?: 'receivables' | 'carrier'
}

const InvoiceDocumentsDialog = ({
  orderId,
  invoiceId,
  invoiceType,
  open,
  handleClose,
  variant = 'receivables',
}: InvoiceDocumentsDialogProps) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isCarrier = variant === 'carrier'
  const includePaymentDocuments =
    isCarrier || invoiceType === 'NOT_PAID' || invoiceType === 'PAID'

  const { data: documents, refetch: refetchDocuments } =
    useListInvoicedocumentsQuery({
      orderId: orderId ?? skipToken,
    })

  const { data: paymentDocuments, refetch: refetchPaymentDocuments } =
    useListPaymentDocumentsQuery(
      includePaymentDocuments && orderId ? { orderId } : skipToken
    )

  const mergedDocuments = useMemo(() => {
    if (!documents) return documents

    const merged: ListDocumentsResponse = { ...documents }

    if (includePaymentDocuments) {
      merged.REMITTANCE = paymentDocuments?.REMITTANCE ?? []
      merged.PAYMENT_RECEIPT = paymentDocuments?.PAYMENT_RECEIPT ?? []
      merged.OTHER = paymentDocuments?.OTHER ?? []
    }

    return merged
  }, [documents, paymentDocuments, includePaymentDocuments])

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      {!!mergedDocuments && (
        <InvoiceDocumentsTable
          documents={mergedDocuments}
          orderId={orderId}
          invoiceId={invoiceId}
          invoiceType={invoiceType}
          variant={variant}
          handleClose={handleClose}
          refetchDocuments={refetchDocuments}
          refetchPaymentDocuments={refetchPaymentDocuments}
        />
      )}
    </Dialog>
  )
}

export default InvoiceDocumentsDialog
